import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";

import { useT } from "@/3-features/i18n/useT";
import { solutionPath, VALUE_PROPS, type VpSlug } from "@/3-features/solutions/valueProps";
import { useOnScrollReveal } from "@/4-components/animations/anime";
import { SectionHead } from "@/4-components/primitives/SectionHead";
import { TiltCard } from "@/4-components/primitives/TiltCard";
import { DISTANCE, STAGGER } from "@/5-shared/constants/motion";
import { Link } from "@/5-shared/routing";

import { CanvasBackdrop, type Variant } from "./art/CanvasBackdrop";
import { EbitdaMini } from "./art/EbitdaMini";
import { HedgeMini } from "./art/HedgeMini";
import { NetCostMini } from "./art/NetCostMini";
import { ProfileMini } from "./art/ProfileMini";
import { StandardsMini } from "./art/StandardsMini";

/**
 * Each bento card previews the signature animation of the page it leads to: `art` is a
 * full-size, looping version of the same scene, filling the card's graphic area under the
 * title, the card's subject, not a watermark behind the copy. The scenes themselves are
 * scroll-scrubbed, so a thumbnail can't mount them; what it reuses is their data and
 * geometry (`standardsModel`, `waterfall`, `hedgeModel`, `italy`), which is why those
 * live in a shared layer. `variant` names the canvas backdrop drawn on the flat plane
 * behind the tilting card (`art/CanvasBackdrop`).
 */
const BENTO: Record<VpSlug, { variant: Variant; art: ReactNode }> = {
  "riduzione-prevedibile": { variant: "descend", art: <NetCostMini /> },
  conformita: { variant: "contours", art: <StandardsMini /> },
  ebitda: { variant: "ascend", art: <EbitdaMini /> },
  "copertura-naturale": { variant: "calm", art: <HedgeMini /> },
  impatto: { variant: "globe", art: <ProfileMini /> },
};

/* five value propositions, widths: 2/3, 1/3 · 1/3, 1/3, 1/3
 *
 * Each card is a chip + title with a redirect icon in the corner, and under them the
 * scene animation at full size: the graphic carries the proposition, the title names it.
 * That is also why there is no "Scopri di più" row, the icon says it in the space the
 * graphic would otherwise lose. */

export function CompaniesBento() {
  const ref = useOnScrollReveal<HTMLElement>({ delayStep: STAGGER.tight, y: DISTANCE.reveal });
  const { t: tr } = useT(["companies-home", "common"]);
  return (
    <section ref={ref} id="vantaggi" className="section bg-zinc-50">
      <div className="shell">
        <SectionHead reveal className="section-head--centered mb-10" eyebrow={tr("bento.eyebrow")} heading={tr("bento.title")} sub={tr("bento.subtitle")} />
        <div className="grid grid-cols-1 lg:grid-cols-3 auto-rows-fr gap-5">
          {VALUE_PROPS.map((t) => (
            // il <canvas> sta su un piano *piatto* dietro il tilt: il tilt è un transform 3D
            // che rasterizza il suo sottoalbero, e un <canvas> lì dentro sfoca. Così invece
            // la cornice (bordo + ombra + contenuto vettoriale) si inclina e si stacca dal
            // fondo, che resta nitido, profondità reale, niente blur.
            <div key={t.slug} data-reveal className={`group relative h-full ${t.span}`}>
              <div className="absolute inset-0 rounded-3xl overflow-hidden bg-white">
                <CanvasBackdrop variant={BENTO[t.slug].variant} />
              </div>

              <TiltCard className="relative z-10 h-full" max={5}>
                <div className="card card-hover h-full overflow-hidden bg-transparent">
                  <Link to={solutionPath(t)} className="flex flex-col h-full p-7">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="inline-flex w-fit items-center gap-2 rounded-lg bg-dec-green/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider whitespace-nowrap text-dec-green-dark">
                          <t.icon className="w-4 h-4 shrink-0" />
                          <span>{tr(`common:nav.solutionCategories.${t.slug}`)}</span>
                        </span>
                        <h3 className="card-title leading-tight mt-3">{tr(`common:nav.solutionLabels.${t.slug}`)}</h3>
                      </div>
                      <span
                        aria-hidden="true"
                        className="grid shrink-0 place-items-center w-10 h-10 rounded-xl bg-dec-green text-white transition-colors group-hover:bg-dec-green-dark"
                      >
                        <ArrowUpRight className="w-5 h-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      </span>
                    </div>

                    <div className="mt-7 flex min-h-[13.125rem] flex-1 md:min-h-[15.625rem]">{BENTO[t.slug].art}</div>
                  </Link>
                </div>
              </TiltCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
